import {Aufgabe} from './aufgabe.model';
import {IFaehigkeit} from './faehigkeit.model';
import {IProfileItemModel, ProfileItemModel} from './profile-item.model';
import {Requests} from './request.model';

/** NP20-1057: Ein Entwurf ist privat; Export und Dritte sehen nur den freigegebenen Stand. */
export type ProjectReleaseStatus = 'DRAFT' | 'RELEASED';

export interface IProjekt extends IProfileItemModel {
  uuid?: string;
  beginn?: Date | string;
  ende?: Date;
  kunde?: string;
  projektname?: string;
  stelle?: string;
  forExport?: boolean;
  tasks?: Aufgabe[];
  beschreibung?: string;
  methodik?: string[];
  faehigkeiten?: IFaehigkeit[];
  branchNames?: Requests[];
  teamSize?: number;
  numberTester?: number;
  projectSize?: number | null;
  workLocation?: string;
  /** Fehlt das Feld, gilt das Projekt als freigegeben. */
  releaseStatus?: ProjectReleaseStatus | null;
  draftSavedAt?: string | null;
  /** false nur für einen Entwurf, der noch nie freigegeben wurde — er fehlt im Export ganz. */
  hasReleasedVersion?: boolean;
  /** Sicht Dritter: der Besitzer hat einen Zwischenstand über diesem Projekt — bis zur Freigabe gesperrt. */
  hasDraft?: boolean;
}

/** Problem-Typ, mit dem das BE Speichern/Löschen durch Dritte über einem Entwurf ablehnt. */
const DRAFT_IN_PROGRESS_TYPE = '/project-draft-in-progress';

export function isDraftInProgressError(err: unknown): boolean {
  const type = (err as {error?: {type?: unknown}})?.error?.type;
  return typeof type === 'string' && type.endsWith(DRAFT_IN_PROGRESS_TYPE);
}

export function isDraft(projekt: IProjekt | null | undefined): boolean {
  return projekt?.releaseStatus === 'DRAFT';
}

/** Ein nie freigegebener Entwurf hat keinen Stand, der exportiert werden könnte. */
export function isExportable(projekt: IProjekt | null | undefined): boolean {
  return projekt?.hasReleasedVersion !== false;
}

export class Projekt extends ProfileItemModel implements IProjekt {
  constructor(
    public uuid?: string,
    public beginn?: Date,
    public ende?: Date,
    public kunde?: string,
    public projektname?: string,
    public stelle?: string,
    public forExport?: boolean,
    public tasks?: Aufgabe[],
    public beschreibung?: string,
    public methodik?: string[],
    public faehigkeiten?: IFaehigkeit[],
    public branchNames?: Requests[],
    public teamSize?: number,
    public numberTester?: number,
    public projectSize?: number | null,
    public workLocation?: string,
    public releaseStatus?: ProjectReleaseStatus | null,
    public draftSavedAt?: string | null,
    public hasReleasedVersion?: boolean,
    public hasDraft?: boolean
  ) {
    super();
    this.uuid = uuid ? uuid : undefined;
    this.beginn = beginn ? beginn : new Date(Date.now());
    this.ende = ende ? ende : undefined;
    this.kunde = kunde ? kunde : undefined;
    this.projektname = projektname ? projektname : undefined;
    this.stelle = stelle ? stelle : undefined;
    this.forExport = forExport ? forExport : true;
    this.tasks = tasks ? tasks : [];
    this.beschreibung = beschreibung ? beschreibung : undefined;
    this.methodik = methodik ? methodik : [];
    this.faehigkeiten = faehigkeiten ? faehigkeiten : [];
    this.branchNames = branchNames ? branchNames : [];
    // NP20-910: Ein neues Projekt startet mit leeren Zahlenfeldern, nicht mit einer ungueltigen 0.
    this.numberTester = numberTester ?? undefined;
    this.teamSize = teamSize ?? undefined;
    this.projectSize = projectSize || null;
    this.workLocation = workLocation ? workLocation : '';
  }

  static emptyObject(): Projekt {
    const data = new Projekt();
    data.uuid = undefined;
    data.beginn = new Date();
    data.ende = new Date();
    data.kunde = '';
    data.projektname = '';
    data.stelle = '';
    data.forExport = true;
    data.tasks = [];
    data.beschreibung = '';
    data.methodik = [];
    data.faehigkeiten = [];
    return data;
  }

  static fromJson(json: IProjekt): Projekt {
    const data = new Projekt();

    data.uuid = json.uuid ?? undefined;
    data.kunde = json.kunde ?? '';
    data.projektname = json.projektname ?? '';
    data.stelle = json.stelle ?? '';
    data.forExport = json.forExport ?? true;

    data.beginn = this.anyToDate(json.beginn);
    data.ende = this.anyToDate(json.ende);

    data.tasks = (json.tasks ?? []).map(
      (t) => new Aufgabe(t['caption'], t['details'], t['skills'] ?? [], t['uuid'])
    );

    data.beschreibung = json.beschreibung ?? '';
    data.methodik = [...(json.methodik ?? [])];
    data.faehigkeiten = [...(json.faehigkeiten ?? [])];

    // NP20-910: Nicht gesetzte Zahlen bleiben leer; eine 0 vom Backend bleibt erhalten.
    data.numberTester = json.numberTester ?? undefined;
    data.teamSize = json.teamSize ?? undefined;
    data.projectSize = json.projectSize || null;
    data.branchNames = (json.branchNames ?? []).map(b => ({...b}));
    data.workLocation = json.workLocation ?? '';
    data.releaseStatus = json.releaseStatus ?? undefined;
    data.draftSavedAt = json.draftSavedAt ?? undefined;
    data.hasReleasedVersion = json.hasReleasedVersion ?? undefined;
    data.hasDraft = json.hasDraft ?? undefined;

    return data;
  }

  newInstance<T extends ProfileItemModel>(): T {
    return new Projekt() as T;
  }

  isEmpty(): boolean {
    return !(
      this.kunde ||
      this.projektname ||
      this.stelle ||
      this.beschreibung ||
      this.tasks ||
      this.methodik ||
      this.faehigkeiten
    );
  }

  static clone(project: IProjekt | Projekt): Projekt {
    return Projekt.fromJson(project);
  }

  static cloneArray(projects: (IProjekt | Projekt)[]): Projekt[] {
    return (projects ?? []).map(p => Projekt.fromJson(p));
  }

  /** NP20-781: Backend-Reihenfolge im FE nachbilden — laufend zuerst, dann Ende DESC, Tiebreak Beginn DESC. */
  static compareByRecency(a: IProjekt, b: IProjekt): number {
    const aEnd = Projekt.toTime(a?.ende);
    const bEnd = Projekt.toTime(b?.ende);
    if ((aEnd === null) !== (bEnd === null)) return aEnd === null ? -1 : 1;
    if (aEnd !== null && bEnd !== null && aEnd !== bEnd) return bEnd - aEnd;
    const aBeg = Projekt.toTime(a?.beginn) ?? Number.NEGATIVE_INFINITY;
    const bBeg = Projekt.toTime(b?.beginn) ?? Number.NEGATIVE_INFINITY;
    // Gleichstand explizit auf 0, sonst liefert -Infinity - -Infinity ein NaN an sort().
    return aBeg === bBeg ? 0 : bBeg - aBeg;
  }

  private static toTime(value: Date | string | null | undefined): number | null {
    if (!value) return null;
    const time = value instanceof Date ? value.getTime() : new Date(value).getTime();
    return Number.isNaN(time) ? null : time;
  }
}
